import React, { useRef, useState } from "react";

import { snakeCase } from "lodash";

import { productAnalytics, useTranslation } from "utils";

import { CurrencyCode, SHOW_SOIL_SIGHT_FIELD_SUMMARY } from "consts";

import { SoilSightFieldSoil } from "models";

import { Button, Icon, Tooltip, Typography } from "components";

import { BetaBadge, BetaCaveat, BetaFooter } from "pages/app/scenario-analysis/components/BetaNotice";
import { EconomicsSection } from "pages/app/scenario-analysis/components/EconomicsSection";
import { FieldSummarySection } from "pages/app/scenario-analysis/components/FieldSummarySection";
import { ForecastPrimaryAction, ForecastSettingsBar } from "pages/app/scenario-analysis/components/ForecastSettingsBar";
import {
  ScenarioAnalysisTab,
  ScenarioAnalysisTabBar,
  ScenarioAnalysisTabFlags,
  tabId,
  tabPanelId,
} from "pages/app/scenario-analysis/components/ScenarioAnalysisTabBar";
import { ScenarioComparisonBox } from "pages/app/scenario-analysis/components/ScenarioComparisonBox";
import { SoilSightOutcomesTour } from "pages/app/scenario-analysis/components/SoilSightOutcomesTour";
import { BetaDrawer } from "pages/app/scenario-analysis/components/beta-drawer/BetaDrawer";
import { useSoilSightOutcomesTour } from "pages/app/scenario-analysis/hooks/useSoilSightOutcomesTour";
import { ScenarioData, ScenarioSettings } from "pages/app/scenario-analysis/types/scenario";
import { SoilSightOutcomesByScenario, ViewMode } from "pages/app/scenario-analysis/utils/forecast";
import { ScenarioResilienceByScenarioId } from "pages/app/scenario-analysis/utils/scenarioResilienceScore";

export interface ScenarioAnalysisViewProps {
  activeTab: ScenarioAnalysisTab;
  onTabChange: (tab: ScenarioAnalysisTab) => void;
  changeDots?: ScenarioAnalysisTabFlags;

  settings: ScenarioSettings;
  /** On an output tab the container runs the pick now. On the builder tab the dots carry it. */
  onSettingsChange: (settings: ScenarioSettings) => void;
  primaryAction: ForecastPrimaryAction | null;
  onPrimaryAction: () => void;
  primaryActionBlockedReason?: string | null;
  onAddScenario: () => void;
  onEditScenarios: () => void;
  onMoreFilters: () => void;
  disableAddScenario: boolean;

  scenarios: ScenarioData[];
  soilSightOutcomes: SoilSightOutcomesByScenario | null;
  projectionYears: number;
  currency: CurrencyCode | null;
  /** Resilience scores keyed by scenario id, for the resilience key-insight card. */
  resilienceScores: ScenarioResilienceByScenarioId;
  isStale: boolean;
  isCalculating: boolean;
  comparisonScenarioId: string | null;
  onSelectComparisonScenario: (scenarioId: string) => void;
  onAddBaselineRotation: () => void;
  onRecalculate: () => void;
  /** Receives the compared scenario, so the insights modal opens on the same one. */
  onGetMoreKeyInsights: (scenarioId: string | null) => void;
  /** A key-insight card was clicked: switches the chart to that view and scrolls it into position. */
  onOpenChartView: (view: ViewMode, scenarioId?: string) => void;
  /** The view the chart is showing, so the card that opens it is marked as the current one. */
  activeChartView?: ViewMode;
  /** Year-zero soil readings for the field; null until a run has reported them. */
  fieldSoil: SoilSightFieldSoil | null;
  onExplainFieldSoil: () => void;
  onEditEconomicInputs: () => void;
  economicsRef?: React.Ref<HTMLDivElement>;
  /** Opens the printable scenario report. */
  onDownloadPdf: () => void;
  /** Resilience scores are refetching after a run, so the report would print the previous scores. */
  isFetchingResilience?: boolean;
  /** The chart, per-year table and financial breakdown. Runs full width. */
  resultsSection: React.ReactNode;
  sideBySideSection: React.ReactNode;
  detailedCalculationsSection: React.ReactNode;

  /** The crop-rotation builder; stays mounted so Add/Edit scenario can drive it through its ref. */
  builderSection: React.ReactNode;
}

// The barrel's Button forwards no ref, so "Show me around" is focused by id.
const SHOW_ME_AROUND_ID = "scenario-analysis-show-me-around";

// Whatever opened the tour, focus goes back to "Show me around": it also opens the tour on an automatic start.
const focusShowMeAround = () => document.getElementById(SHOW_ME_AROUND_ID)?.focus({ preventScroll: true });

/** Panels stay mounted, because refs, scroll targets and chart state must survive a tab switch. */
const TabPanel = ({ tab, activeTab, children }: { tab: ScenarioAnalysisTab; activeTab: ScenarioAnalysisTab; children: React.ReactNode }) => (
  <div
    id={tabPanelId(tab)}
    role="tabpanel"
    aria-labelledby={tabId(tab)}
    className={`scenario-analysis-view-panel${tab === activeTab ? "" : " scenario-analysis-view-panel--hidden"}`}
    data-testid={`scenario-analysis-view-panel-${tab}`}
  >
    {children}
  </div>
);

/**
 * Scenario analysis page layout. Presentational — every handler comes from `ScenarioAnalysis`, so
 * this view shares the page's state and mutations rather than duplicating them. This view holds only the
 * open state of the beta drawer.
 */
export const ScenarioAnalysisView = ({
  activeTab,
  onTabChange,
  changeDots,
  settings,
  onSettingsChange,
  primaryAction,
  onPrimaryAction,
  primaryActionBlockedReason = null,
  onAddScenario,
  onEditScenarios,
  onMoreFilters,
  disableAddScenario,
  scenarios,
  soilSightOutcomes,
  projectionYears,
  currency,
  resilienceScores,
  isStale,
  isCalculating,
  comparisonScenarioId,
  onSelectComparisonScenario,
  onAddBaselineRotation,
  onRecalculate,
  onGetMoreKeyInsights,
  onOpenChartView,
  activeChartView,
  fieldSoil,
  onExplainFieldSoil,
  onEditEconomicInputs,
  economicsRef,
  onDownloadPdf,
  isFetchingResilience = false,
  resultsSection,
  sideBySideSection,
  detailedCalculationsSection,
  builderSection,
}: ScenarioAnalysisViewProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const [isBetaDrawerOpen, setIsBetaDrawerOpen] = useState(false);
  const openBetaDrawer = () => setIsBetaDrawerOpen(true);
  // We check rows, not keys. A scenario with no years keeps its key, and its tab shows an empty state.
  const hasOutcomeRows = Object.values(soilSightOutcomes ?? {}).some((rows) => rows.length > 0);

  const isPdfDataLoading = isCalculating || isFetchingResilience;
  const isDownloadPdfBlocked = isStale || isPdfDataLoading;
  const downloadPdfBlockedReason = isPdfDataLoading ? t("forecastSettingsBar.calculating") : isStale ? t("outcomeComparison.staleBody") : null;

  const layoutRef = useRef<HTMLDivElement>(null);
  const stickyBarRef = useRef<HTMLDivElement>(null);
  const tour = useSoilSightOutcomesTour({
    activeTab,
    goToTab: onTabChange,
    planCount: scenarios.length,
    isReady: !isCalculating,
    returnFocus: focusShowMeAround,
  });

  // Only a tab-bar choice can start a chapter. Tabs opened by an action go through `onTabChange` directly.
  const handleTabBarSelect = (tab: ScenarioAnalysisTab) => {
    if (tab !== activeTab) productAnalytics.track("soilsights:tab_view", { tab: snakeCase(tab) });
    onTabChange(tab);
    tour.onTabBarSelect(tab);
  };

  return (
    <div className="scenario-analysis-view-layout" data-testid="scenario-analysis-view" ref={layoutRef}>
      <div className="scenario-analysis-view-header">
        <div className="scenario-analysis-view-title">
          <Typography variant="h2">{t("scenarioBuilder.pageTitle")}</Typography>
          <BetaBadge onClick={openBetaDrawer} />
        </div>

        <div className="scenario-analysis-view-header-actions">
          <Button
            type="link"
            id={SHOW_ME_AROUND_ID}
            className="scenario-analysis-view-show-me-around"
            dataTestId="scenario-analysis-show-me-around-btn"
            onClick={tour.showMeAround}
          >
            <Icon iconName="tabler/compass" size="s" />
            {t("outcomesTour.showMeAround")}
          </Button>

          {/* Stale or in-flight figures would print without the warning the page shows beside them. */}
          <Tooltip title={downloadPdfBlockedReason} placement="top">
            <Button
              type="link"
              className="scenario-analysis-view-download-pdf"
              dataTestId="scenario-analysis-download-pdf-btn"
              onClick={onDownloadPdf}
              disabled={isDownloadPdfBlocked}
            >
              <Icon iconName="tabler/printer" size="s" />
              {t("scenarioBuilder.downloadPdfReport")}
            </Button>
          </Tooltip>
        </div>
      </div>

      {/* Tabs and settings share one sticky block, so the Recalculate button never scrolls out of view. */}
      <div className="scenario-analysis-view-settings-sticky" ref={stickyBarRef}>
        <ScenarioAnalysisTabBar activeTab={activeTab} onChange={handleTabBarSelect} changeDots={changeDots} />

        <ForecastSettingsBar
          settings={settings}
          onSettingsChange={onSettingsChange}
          primaryAction={primaryAction}
          onPrimaryAction={onPrimaryAction}
          primaryActionBlockedReason={primaryActionBlockedReason}
          onAddScenario={onAddScenario}
          onEditScenarios={onEditScenarios}
          onMoreFilters={onMoreFilters}
          disableAddScenario={disableAddScenario}
          showScenarioActions={activeTab !== "scenarios"}
        />
      </div>

      <TabPanel tab="scenarios" activeTab={activeTab}>
        {builderSection}
      </TabPanel>

      <TabPanel tab="worth-it" activeTab={activeTab}>
        <ScenarioComparisonBox
          scenarios={scenarios}
          soilSightOutcomes={soilSightOutcomes}
          projectionYears={projectionYears}
          currency={currency}
          resilienceScores={resilienceScores}
          isStale={isStale}
          isCalculating={isCalculating}
          selectedScenarioId={comparisonScenarioId}
          onSelectScenario={onSelectComparisonScenario}
          onAddScenario={onAddScenario}
          onAddBaselineRotation={onAddBaselineRotation}
          onRecalculate={onRecalculate}
          onGetMoreKeyInsights={onGetMoreKeyInsights}
          onOpenChartView={onOpenChartView}
          activeChartView={activeChartView}
          onOpenBetaInfo={openBetaDrawer}
        />

        {resultsSection}

        <div ref={economicsRef}>
          <EconomicsSection onEditEconomicInputs={onEditEconomicInputs} />
        </div>

        {SHOW_SOIL_SIGHT_FIELD_SUMMARY && <FieldSummarySection fieldSoil={fieldSoil} onExplainFieldSoil={onExplainFieldSoil} />}

        {sideBySideSection}
      </TabPanel>

      <TabPanel tab="detailed-calcs" activeTab={activeTab}>
        {hasOutcomeRows && <BetaCaveat onOpenBetaInfo={openBetaDrawer} />}
        {detailedCalculationsSection}
      </TabPanel>

      <BetaFooter onOpenBetaInfo={openBetaDrawer} />
      <BetaDrawer visible={isBetaDrawerOpen} onClose={() => setIsBetaDrawerOpen(false)} />
      {tour.card ? <SoilSightOutcomesTour {...tour.card} rootRef={layoutRef} stickyBarRef={stickyBarRef} /> : null}
    </div>
  );
};

export default ScenarioAnalysisView;
